import React, { useEffect } from 'react';
import { DataGridPro, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';

interface ClientsDataTableProps {
    rows: ClientTableRow[];
    loading: boolean;
    selectedRows: GridRowSelectionModel;
    onSelectionChange: (selectionModel: GridRowSelectionModel) => void;
    onFetchData: () => Promise<void>;
    onOpenContextMenu: (event: React.MouseEvent, rowId?: string) => void;
}

const ClientsDataTable: React.FC<ClientsDataTableProps> = ({
    rows,
    loading,
    selectedRows,
    onSelectionChange,
    onFetchData,
    onOpenContextMenu,
}) => {
    const { t } = useTranslation();

    const columns: GridColDef<ClientTableRow>[] = [
        {
            field: 'client_id',
            headerName: t('user_management.clients.table.columns.client_id'),
            flex: 1,
            minWidth: 180,
        },
        {
            field: 'name',
            headerName: t('user_management.clients.table.columns.name'),
            flex: 1,
            minWidth: 180,
        },
        {
            field: 'owner_id',
            headerName: t('user_management.clients.table.columns.owner_id'),
            flex: 0.6,
            minWidth: 120,
        },
        {
            field: 'grant_types',
            headerName: t('user_management.clients.table.columns.grant_types'),
            flex: 1,
            minWidth: 220,
        },
        {
            field: 'scope',
            headerName: t('user_management.clients.table.columns.scope'),
            flex: 1,
            minWidth: 220,
        },
        {
            field: 'redirect_uri',
            headerName: t('user_management.clients.table.columns.redirect_uri'),
            flex: 1.5,
            minWidth: 300,
        },
    ];

    useEffect(() => {
        onFetchData();
    }, [onFetchData]);

    return (
        <div
            style={{ width: '100%', height: '100%' }}
            onContextMenu={(event: React.MouseEvent<HTMLDivElement>) => {
                event.preventDefault();

                const target = event.target as HTMLElement;
                const rowElement = target.closest('.MuiDataGrid-row');
                const rowId = rowElement?.getAttribute('data-id') ?? undefined;

                onOpenContextMenu(event, rowId);
            }}
        >
            <DataGridPro
                columns={columns}
                rows={rows}
                loading={loading}
                disableColumnMenu
                pagination
                checkboxSelection
                onRowSelectionModelChange={onSelectionChange}
                rowSelectionModel={selectedRows}
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'name', sort: 'asc' }],
                    },
                }}
                showCellVerticalBorder
                showColumnVerticalBorder
                headerFilters
                localeText={{
                    headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
                    headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
                    headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
                    headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
                    headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
                    headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
                    footerRowSelected: (count) =>
                        count > 1
                            ? t('general.table.footerFilters.footerRowSelected.>1', { count })
                            : t('general.table.footerFilters.footerRowSelected.=1', { count }),
                    footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                    MuiTablePagination: {
                        labelDisplayedRows: ({ from, to, count }) =>
                            t('general.table.footerFilters.labelDisplayedRows', {
                                from,
                                to,
                                count,
                            }),
                        labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
                    },
                }}
                sx={{
                    height: 'calc(100vh - 140px)',
                }}
            />
        </div>
    );
};

export { ClientsDataTable };
